import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from '../../i18n'
import { userApi } from '../../api'
import type { UserRelation, UserRelationStatus } from '../../types/api'
import { useTheme } from '../../hooks/useTheme'
import { USER_RELATION_STATUS } from '../../constants'
import { translateUserRelationStatus } from '../../utils/statusTranslations'
import { Button, Badge, ErrorMessage } from '../ui'
import { UserRelationCard } from './UserRelationCard'

export default function TraineesList() {
  const navigate = useNavigate()
  const theme = useTheme()
  const { t, translate } = useTranslation()
  const [searchParams] = useSearchParams()

  const [trainees, setTrainees] = useState<UserRelation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<UserRelationStatus | 'ALL'>('ALL')

  useEffect(() => {
    const filterParam = searchParams.get('filter')
    if (filterParam === 'active') {
      setStatusFilter('ACTIVE')
    } else if (filterParam === 'pendingByTrainee') {
      setStatusFilter('PENDING_APPROVAL_BY_TRAINEE')
    } else if (filterParam === 'pendingByCoach') {
      setStatusFilter('PENDING_APPROVAL_BY_COACH')
    }
  }, [searchParams])

  useEffect(() => {
    loadData()
  }, [])

  async function loadData() {
    setLoading(true)
    setError(null)
    try {
      const traineesList = await userApi.getTrainees()
      setTrainees(traineesList)
    } catch (err) {
      console.error('Error loading trainees:', err)
      setError(err instanceof Error ? err.message : 'Failed to load trainees')
    } finally {
      setLoading(false)
    }
  }

  const filteredTrainees = trainees.filter(trainee => {
    const query = searchQuery.toLowerCase()
    const matchesSearch = (
      trainee.username?.toLowerCase().includes(query) ||
      trainee.firstName?.toLowerCase().includes(query) ||
      trainee.lastName?.toLowerCase().includes(query)
    )
    const matchesStatus = statusFilter === 'ALL' || trainee.relationStatus === statusFilter
    return matchesSearch && matchesStatus
  })

  const inviteLabel = (t.trainees as { inviteButton?: string }).inviteButton ?? t.dashboard.quickActions.inviteTrainee ?? t.invite.title

  const filters: { label: string; value: UserRelationStatus | 'ALL' }[] = [
    { label: t.trainees.filters.all, value: 'ALL' },
    { label: t.trainees.filters.active, value: USER_RELATION_STATUS.ACTIVE },
    { label: t.trainees.filters.pendingByTrainee, value: USER_RELATION_STATUS.PENDING_APPROVAL_BY_TRAINEE },
    { label: t.trainees.filters.declined, value: USER_RELATION_STATUS.DECLINED_BY_TRAINEE },
  ]

  return (
    <div className="p-5">
      <header className="mb-6">
        <h1 className="text-2xl font-bold mb-1" style={{ color: theme.text.primary }}>
          {t.trainees.title}
        </h1>
        <p className="text-sm" style={{ color: theme.text.muted }}>
          {translate('trainees.total', { count: trainees.length })}
        </p>
      </header>

      <div className="mb-4 relative">
        <span className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: theme.text.muted }}>
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
        </span>
        <input
          type="text"
          placeholder={t.trainees.searchPlaceholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full rounded-xl pl-10 pr-4 py-3 text-base outline-none transition-all"
          style={{
            backgroundColor: theme.inputBg,
            color: theme.text.primary,
            border: `1px solid ${theme.inputBorder}`,
          }}
        />
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1 mb-4" style={{ scrollbarWidth: 'none' }}>
        {filters.map(f => (
          <button
            key={f.value}
            onClick={() => setStatusFilter(f.value)}
            className="px-3.5 py-1.5 rounded-full text-xs font-semibold flex-shrink-0 transition-colors"
            style={statusFilter === f.value
              ? { backgroundColor: theme.buttonColor, color: theme.buttonTextColor }
              : { backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}`, color: theme.text.muted }
            }
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mb-6">
        <Button
          variant="primary"
          size="md"
          fullWidth
          onClick={() => navigate('/invite')}
        >
          {inviteLabel}
        </Button>
      </div>

      {loading ? (
        <div className="flex flex-col gap-3">
          {[1, 2, 3].map(i => (
            <div
              key={i}
              className="rounded-2xl p-4 animate-pulse"
              style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}` }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-14 h-14 rounded-full flex-shrink-0"
                  style={{ backgroundColor: `${theme.text.muted}25` }}
                />
                <div className="flex-1 space-y-2">
                  <div className="h-3 rounded-full w-28" style={{ backgroundColor: `${theme.text.muted}25` }} />
                  <div className="h-2.5 rounded-full w-20" style={{ backgroundColor: `${theme.text.muted}18` }} />
                  <div className="h-5 rounded-md w-16" style={{ backgroundColor: `${theme.text.muted}18` }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <ErrorMessage
          title="Error"
          message={error}
          icon="⚠️"
          onRetry={loadData}
        />
      ) : filteredTrainees.length > 0 ? (
        <div className="flex flex-col gap-3">
          {filteredTrainees.map(trainee => {
            const displayName = trainee.firstName
              ? `${trainee.firstName} ${trainee.lastName || ''}`.trim()
              : trainee.username || 'Unknown'

            return (
              <UserRelationCard
                key={trainee.id}
                displayName={displayName}
                username={trainee.username}
                photoUrl={trainee.photoUrl}
                fallbackBg={theme.accent.light}
                onClick={() => navigate(`/trainee/${trainee.id}`)}
                badges={
                  <>
                    <Badge
                      variant={trainee.relationStatus === 'ACTIVE' ? 'success' : trainee.relationStatus?.includes('PENDING') ? 'warning' : 'info'}
                      size="sm"
                      className="rounded-md flex-shrink-0"
                    >
                      {translateUserRelationStatus(trainee.relationStatus, t)}
                    </Badge>
                    {trainee.isVirtual && (
                      <Badge variant="brand" size="sm" className="rounded-md flex-shrink-0">
                        {t.trainees.virtualBadge}
                      </Badge>
                    )}
                  </>
                }
              />
            )
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center py-14 text-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${theme.buttonColor}15` }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: theme.buttonColor }}>
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <h3 className="text-base font-semibold mb-1" style={{ color: theme.text.primary }}>
            {t.trainees.emptySearch.title}
          </h3>
          <p className="text-sm px-8 mb-6" style={{ color: theme.text.muted }}>
            {searchQuery ? t.trainees.emptySearch.tryDifferent : t.trainees.emptySearch.startInviting}
          </p>
          {!searchQuery && (
            <Button
              variant="primary"
              size="md"
              onClick={() => navigate('/invite')}
            >
              {t.dashboard.emptyState.action}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
